import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { BadgeCheck, BookOpen, CreditCard, Gauge, LifeBuoy, LogOut, Menu, ScrollText, Search, Settings2, ShieldCheck, SlidersHorizontal, UserCog, Users, X } from 'lucide-react';
import type { AdminPerm } from '@dd/shared';
import { cn } from '@dd/ui';
import { adminMay, useAuth } from '@/lib/auth';
import { useGet } from '@/lib/hooks';
import { Avatar, RoleBadge } from '@/components/ui';

interface Item { to: string; label: string; icon: typeof Gauge; perm: AdminPerm; end?: boolean; badge?: 'payments' | 'approvals' | 'tickets' }
const GROUPS: Array<{ title: string; items: Item[] }> = [
  { title: 'Operate', items: [
    { to: '/', label: 'Overview', icon: Gauge, perm: 'overview', end: true },
    { to: '/accounts', label: 'Customers', icon: Users, perm: 'account_search' },
    { to: '/tickets', label: 'Support inbox', icon: LifeBuoy, perm: 'ticket_reply', badge: 'tickets' },
    { to: '/governance', label: 'Approvals & requests', icon: ShieldCheck, perm: 'trial_abuse', badge: 'approvals' },
  ] },
  { title: 'Money', items: [
    { to: '/payments-verify', label: 'Payments to verify', icon: BadgeCheck, perm: 'payment_verify', badge: 'payments' },
    { to: '/billing', label: 'Billing & SMS', icon: CreditCard, perm: 'refund_approve' },
  ] },
  { title: 'Platform', items: [
    { to: '/content', label: 'Content', icon: BookOpen, perm: 'content_manage' },
    { to: '/system', label: 'System health', icon: Settings2, perm: 'feature_flag' },
    { to: '/settings', label: 'Keys & settings', icon: SlidersHorizontal, perm: 'payment_settings' },
  ] },
  { title: 'Team', items: [
    { to: '/audit', label: 'Audit log', icon: ScrollText, perm: 'audit_log' },
    { to: '/admins', label: 'Admins & roles', icon: UserCog, perm: 'admin_manage' },
  ] },
];

interface Counts { pendingPayments?: { count: number }; pendingApprovals?: number; tickets?: { open: number; slaBreached: number } }

export function Shell() {
  const admin = useAuth((s) => s.admin)!;
  const [open, setOpen] = useState(false);
  const idle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const counts = useGet<Counts>('/overview');
  // 30-minute idle timeout (Guide §17.1) — the server enforces it too; this just returns to the login screen promptly
  useEffect(() => {
    const reset = () => { clearTimeout(idle.current); idle.current = setTimeout(() => void useAuth.getState().logout(), 30 * 60_000); };
    const ev = ['pointerdown', 'keydown', 'scroll'] as const;
    ev.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { ev.forEach((e) => window.removeEventListener(e, reset)); clearTimeout(idle.current); };
  }, []);
  const badge = (k?: Item['badge']): number => (k === 'payments' ? counts.data?.pendingPayments?.count ?? 0 : k === 'approvals' ? counts.data?.pendingApprovals ?? 0 : k === 'tickets' ? counts.data?.tickets?.slaBreached ?? 0 : 0);
  const groups = GROUPS.map((g) => ({ ...g, items: g.items.filter((n) => adminMay(admin, n.perm)) })).filter((g) => g.items.length);
  const nav = (
    <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {groups.map((g) => (
        <div key={g.title}>
          <div className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-white/35">{g.title}</div>
          <div className="space-y-0.5">
            {g.items.map((n) => {
              const b = badge(n.badge);
              return (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                  className={({ isActive }) => cn('group relative flex h-10 items-center gap-3 rounded-lg px-3 text-[13.5px] font-semibold transition-colors', isActive ? 'bg-white/10 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white')}>
                  {({ isActive }) => (<>
                    {isActive && <span className="absolute -left-3 top-2 h-6 w-1 rounded-r-full bg-brand" />}
                    <n.icon size={18} className={isActive ? 'text-brand' : 'text-white/45 group-hover:text-white/80'} />
                    <span className="flex-1 truncate">{n.label}</span>
                    {b > 0 && <span className="rounded-full bg-brand px-1.5 text-[11px] font-bold leading-5 text-white">{b}</span>}
                  </>)}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
  const brand = (
    <div className="flex items-center gap-3 px-5 py-5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-lg font-black text-white shadow-lg shadow-brand/30">D</span>
      <div className="leading-tight"><div className="text-[15px] font-extrabold tracking-tight text-white">DokaanDigit</div><div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">NTechEdge · Operations</div></div>
    </div>
  );
  const side = (
    <>
      {brand}{nav}
      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 rounded-xl bg-white/5 p-2.5">
          <Avatar name={admin.name} size={34} />
          <div className="min-w-0 flex-1"><div className="truncate text-[13px] font-bold text-white">{admin.name}</div><div className="mt-0.5"><RoleBadge role={admin.role} /></div></div>
          <button type="button" aria-label="Sign out" title="Sign out" onClick={() => void useAuth.getState().logout()} className="flex h-8 w-8 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white"><LogOut size={16} /></button>
        </div>
        <div className="mt-3 px-1 text-center text-[10px] leading-4 text-white/30">DokaanDigit — a flagship product of NTechEdge</div>
      </div>
    </>
  );
  return (
    <div className="min-h-dvh bg-[#F3F4F7] lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="sticky top-0 hidden h-dvh flex-col bg-[#12141B] lg:flex">{side}</aside>
      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-line/80 bg-white/90 px-4 backdrop-blur lg:px-8">
          <button type="button" aria-label="Menu" onClick={() => setOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-lg hover:bg-neutral-100 lg:hidden"><Menu size={22} /></button>
          {adminMay(admin, 'account_search') ? <QuickSearch /> : <span />}
          <span className="hidden text-xs font-semibold text-ink-3 sm:block">Bangladesh time · all actions are audited</span>
        </header>
        {open && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
            <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-[#12141B] shadow-float">
              <button type="button" aria-label="Close" className="absolute right-3 top-4 text-white/70" onClick={() => setOpen(false)}><X size={20} /></button>
              {side}
            </aside>
          </div>
        )}
        <main className="mx-auto w-full max-w-[1280px] p-4 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}

/** type a name, shop or last digits of a phone → straight to the customer list with that search */
function QuickSearch() {
  const nav = useNavigate();
  const [v, setV] = useState('');
  return (
    <form className="relative w-full max-w-md" onSubmit={(e) => { e.preventDefault(); if (v.trim()) { nav(`/accounts?q=${encodeURIComponent(v.trim())}`); setV(''); } }}>
      <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
      <input value={v} onChange={(e) => setV(e.target.value)} placeholder="Find a customer — name, shop, phone digits, ID" className="h-10 w-full rounded-xl border border-line bg-neutral-50 pl-9 pr-3 text-sm outline-none transition focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/15" />
    </form>
  );
}
